import { useState } from "react";

export type TableOfContentsSection = {
  title: string;
  items?: { label: string; id: string }[];
  id?: string; // Optional ID for sections without items
};

interface TableOfContentsProps {
  sections: TableOfContentsSection[];
  defaultOpenSection?: string;
  onSectionSelect?: (sectionTitle: string) => void;
  title?: string;
  scrollOffset?: number; // Offset in pixels to account for fixed headers
}

export default function TableOfContents({
  sections,
  defaultOpenSection,
  onSectionSelect,
  title = "Contents",
  scrollOffset = 0,
}: TableOfContentsProps) {
  const [openSection, setOpenSection] = useState(
    defaultOpenSection || sections[0]?.title || "",
  );

  const handleScroll = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      // Find the scrollable parent container
      let scrollContainer: HTMLElement | Window = window;
      let parent = el.parentElement;

      while (parent) {
        const overflow = window.getComputedStyle(parent).overflowY;
        if (overflow === "auto" || overflow === "scroll") {
          scrollContainer = parent;
          break;
        }
        parent = parent.parentElement;
      }

      if (scrollContainer === window) {
        // Scrolling the window
        const elementPosition = el.getBoundingClientRect().top;
        const offsetPosition =
          elementPosition + window.pageYOffset - scrollOffset;

        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth",
        });
      } else {
        // Scrolling a container
        const container = scrollContainer as HTMLElement;
        const containerRect = container.getBoundingClientRect();
        const elementRect = el.getBoundingClientRect();
        const relativeTop = elementRect.top - containerRect.top;
        const scrollTop = container.scrollTop + relativeTop - scrollOffset;

        container.scrollTo({
          top: scrollTop,
          behavior: "smooth",
        });
      }
    }
  };

  const handleSectionClick = (sectionTitle: string, sectionId?: string) => {
    // If section has no items and has an ID, scroll to it directly
    if (
      sectionId &&
      !sections.find((s) => s.title === sectionTitle)?.items?.length
    ) {
      handleScroll(sectionId);
      return;
    }

    setOpenSection(openSection === sectionTitle ? "" : sectionTitle);

    if (onSectionSelect && openSection !== sectionTitle) {
      onSectionSelect(sectionTitle);
    }
  };

  return (
    <div className="flex flex-col gap-2 text-neutral-950">
      <h1 className="text-[16px] leading-[24px] font-bold -tracking-wide">
        {title}
      </h1>

      {sections.map((section) => {
        const hasItems = section.items && section.items.length > 0;

        return (
          <div key={section.title} className="">
            {/* Section Title */}
            <h2
              className={`cursor-pointer text-[14px] leading-[24px] font-bold -tracking-wide transition-all duration-300 ${
                !hasItems ? "hover:text-brand-500 hover:underline" : ""
              }`}
              onClick={() => handleSectionClick(section.title, section.id)}
            >
              {section.title}
            </h2>

            {/* Collapsible Content with Animation - only show if section has items */}
            {hasItems && (
              <div
                className={`overflow-hidden transition-all duration-600 ease-in-out ${
                  openSection === section.title
                    ? "max-h-96 translate-y-0 transform opacity-100"
                    : "max-h-0 -translate-y-2 transform opacity-0"
                }`}
              >
                <div className="flex flex-row space-x-2.5">
                  <div className="bg-brand-200 w-[2px] self-stretch"></div>
                  <div className="space-y-2 py-1">
                    {section.items!.map((item) => (
                      <p
                        key={item.id}
                        onClick={() => handleScroll(item.id)}
                        className="hover:text-brand-500 cursor-pointer text-[12px] leading-[16px] font-semibold -tracking-wide transition-colors duration-150 hover:underline"
                      >
                        {item.label}
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
